/**
 * Reset Password Page
 *
 * Reached when the user clicks the link in the "Reset your HighCall Rodeo
 * password" email. Supabase verifies the recovery token and redirects to
 * /auth/reset-password with `#access_token=...&refresh_token=...&type=recovery`
 * in the URL hash.
 *
 * This page extracts those tokens, shows a "set new password" form, then
 * POSTs to /api/auth/recover-password to apply the change. The user is
 * redirected to /login on success.
 */

import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { PublicHeader } from '../../components/layout/PublicHeader';
import { cn } from '../../lib/utils';

const resetSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetFormData = z.infer<typeof resetSchema>;

function parseHashParams(hash: string): Record<string, string> {
  if (!hash || hash.length <= 1) return {};
  const params = new URLSearchParams(hash.substring(1));
  const result: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    result[key] = value;
  }
  return result;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [tokens, setTokens] = useState<{ access: string; refresh: string } | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
  });

  // Extract tokens from URL hash on mount, then clear the hash so the tokens
  // don't sit in the address bar / history.
  useEffect(() => {
    const params = parseHashParams(window.location.hash);
    if (params.type !== 'recovery' || !params.access_token || !params.refresh_token) {
      setTokenError(
        'This link is invalid or has expired. Request a new password reset email.'
      );
      return;
    }
    setTokens({ access: params.access_token, refresh: params.refresh_token });
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const onSubmit = async (data: ResetFormData) => {
    if (!tokens) return;
    setSubmitError(null);
    try {
      setIsSubmitting(true);
      await authService.recoverPassword(tokens.access, tokens.refresh, data.password);
      setIsSuccess(true);
      // Brief pause so the user can read the confirmation, then off to login.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      const message =
        (err as { message?: string })?.message ||
        'Failed to update password. The link may have expired — request a new one.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
            {isSuccess ? (
              <div className="text-center py-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 mb-4">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Password updated
                </h2>
                <p className="text-sm text-muted-foreground">
                  Redirecting you to sign in...
                </p>
              </div>
            ) : tokenError ? (
              <div className="text-center py-4">
                <h2 className="text-2xl font-bold text-foreground mb-2">
                  Reset link invalid
                </h2>
                <p className="text-sm text-muted-foreground mb-6">{tokenError}</p>
                <Link
                  to="/forgot-password"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Request a new link
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-foreground">Set new password</h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Enter your new password below.
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="password" className="text-sm font-medium text-foreground">
                      New Password
                    </label>
                    <input
                      {...register('password')}
                      type="password"
                      id="password"
                      autoComplete="new-password"
                      autoFocus
                      disabled={isSubmitting || !tokens}
                      className={cn(
                        'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        errors.password ? 'border-destructive' : 'border-input'
                      )}
                      placeholder="••••••••"
                    />
                    {errors.password && (
                      <p className="text-sm text-destructive">{errors.password.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="confirmPassword"
                      className="text-sm font-medium text-foreground"
                    >
                      Confirm Password
                    </label>
                    <input
                      {...register('confirmPassword')}
                      type="password"
                      id="confirmPassword"
                      autoComplete="new-password"
                      disabled={isSubmitting || !tokens}
                      className={cn(
                        'flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                        errors.confirmPassword ? 'border-destructive' : 'border-input'
                      )}
                      placeholder="••••••••"
                    />
                    {errors.confirmPassword && (
                      <p className="text-sm text-destructive">
                        {errors.confirmPassword.message}
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p className="text-sm text-destructive">{submitError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !tokens}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to sign in
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
